// types/index.ts

// --- Patient Info (hybrid for backward compatibility) ---
export interface PatientInfo {
  // dateOfBirth est conservé pour la rétrocompatibilité, mais non utilisé dans le code
  dateOfBirth?: string;
  prenom: string;
  nom: string;
  dateNaissance: string;
  telephone: string;
  adresse: string;
  motifConsultation: string;
  dateConsultation: string;
  sexe: string;
  email: string;
  firstName?: string;
  lastName?: string;
  dateVisite?: string;
  age?: string;
  raisonVisite?: string;
  dureeSymptomes: string;
  symptomes?: string;
  allergies: string;
}

// --- Conditions médicales ---
export interface MedicalCondition {
  [key: string]: boolean | string;
}

// Antécédents familiaux & observations systèmes → bool only
export interface BoolCondition {
  [key: string]: boolean;
}

// --- Vie sociale ---
export interface VieSociale {
  animauxMaison: boolean;
  genreAnimaux: string;
  fumeur: boolean;
  cigarettesParJour: string;
  fumeurMaison: boolean;
}

// --- Consultation Actuelle ---
export interface ConsultationActuelle {
  date: string;
  observations: string;
  recommandations: string;
  diagnostics: string;
  traitements: string;
}

// --- Données formulaire consultation ---
export interface FormData {
  patientInfo: PatientInfo;
  conditionsMedicales: MedicalCondition;
  medicaments: string;
  dateDernierBilan: string;
  vieSociale: VieSociale;
  antecedentsFamiliaux: BoolCondition;
  observationsSystemes: BoolCondition;
  consultationActuelle: ConsultationActuelle;
  poids: string;
  taille: string;
  tensionArterielle: string;
  rythmeCardiaque: string;
  temperature: string;
  instructions?: string;
  pediatrieInfo?: PediatricsInfo;
}

// --- Roles ---
export type UserRole = "DOCTOR" | "PATIENT" | "ADMIN" | "SECRETARY";

// --- Financial Info ---
export interface FinancialInfo {
  total: number;
  paid: number;
  due: number;
}

// --- Patient ---
export interface Patient {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  dateOfBirth?: string;
  gender: string;
  address: string;
  phone: string;
  email: string;
  role: "PATIENT";
  dureeSymptomes: string;
  allergies: string;
  instructions?: string;
}

export interface PatientFormData {
  password?: string;
  firstName: string;
  lastName: string;
  username: string;
  dateOfBirth: string;
  gender: string;
  address: string;
  phone: string;
  email: string;
  role: "PATIENT";
}

// --- Doctor ---
export interface Doctor {
  title: string;
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  dateOfBirth: string;
  gender: string;
  address: string;
  phone: string;
  email: string;
  role: "DOCTOR";
  specialty: string;
}

export interface DoctorFormData {
  firstName: string;
  lastName: string;
  username: string;
  dateOfBirth: string;
  gender: string;
  address: string;
  phone: string;
  email: string;
  password?: string;
  role: UserRole;
  specialty: string;
}

// --- Motifs de visite ---
export const VisitReason = {
  GENERAL_CONSULTATION: "GENERAL_CONSULTATION",
  PEDIATRIC_CONSULTATION: "PEDIATRIC_CONSULTATION",
  SPECIALIST_CONSULTATION: "SPECIALIST_CONSULTATION",
  RESPIRATORY_PHYSIOTHERAPY: "RESPIRATORY_PHYSIOTHERAPY",
  PRE_EMPLOYMENT_VISIT: "PRE_EMPLOYMENT_VISIT",
  GYNECOLOGY_CONSULTATION: "GYNECOLOGY_CONSULTATION",
} as const;

export type VisitReason = (typeof VisitReason)[keyof typeof VisitReason];

export const reasonMap: Record<VisitReason, string> = {
  GENERAL_CONSULTATION: "Consultation générale",
  PEDIATRIC_CONSULTATION: "Consultation pédiatrique",
  SPECIALIST_CONSULTATION: "Consultation spécialiste",
  RESPIRATORY_PHYSIOTHERAPY: "Kinésithérapie respiratoire",
  PRE_EMPLOYMENT_VISIT: "Visite pré-emploi",
  GYNECOLOGY_CONSULTATION: "Consultation gynécologique",
};

// --- Statuts ---
export type ConstanteStatus = "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";

// --- Constante (Signes vitaux) ---
export interface MedicalConstant {
  lastName: string | undefined;
  firstName: string | undefined;
  age?: string;
  allergies?: string;
  dureeSymptomes?: string;
  symptomes?: string;
  id: number;
  patientId: number;
  doctorId: number;
  weight?: number;
  height?: number;
  bloodPressure?: string;
  heartRate?: number;
  temperature?: number;
  reason?: VisitReason | string;
  recordedAt?: string;
  status?: ConstanteStatus;
  hasConsultation?: boolean;
  // Legacy/french aliases (optional)
  maladeId?: number;
  poids?: string | number;
  taille?: string | number;
  tensionArterielle?: string;
  frequenceCardiaque?: string | number;
  rythmeCardiaque?: string | number;
  motif?: string;
  date?: string;
  dateVisit?: string;
  dateOfBirth?: string;
  patientPhone?: string;
  phone?: string;
  email?: string;
  address?: string;
  gender?: string;

  // Relations
  patient?: Patient;
  doctor?: Doctor;

  // Infos supplémentaires
  patientFullName?: string;
  doctorName?: string;
  consultationId?: number;
  consultationDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Expose Constante alias for compatibility
export type Constante = MedicalConstant;

// --- Formulaire Constante ---
export interface ConstanteFormData {
  dossierMedicalId: null;
  poids: string;
  taille: string;
  tensionArterielle: string;
  frequenceCardiaque: string;
  temperature: string;
  motif: VisitReason | "";
  doctorId: string | number;
  maladeId: string | number;
  status: ConstanteStatus;
}

// --- Item de prescription simplifié ---
export interface SimplePrescriptionItem {
  name: string;
  quantity?: number;
  quantityUnit?: string;
  dose?: string;
  doseUnit?: string;
  frequency?: string;
  interval?: string;
  duration?: number;
  durationUnit?: string;
  startDate?: string;
  endDate?: string;
  advice?: string;
  instructions?: string;
}

// --- Informations gynécologiques ---
export interface GynecologyInfo {
  // Informations générales
  menopauseAge?: number;
  hysterectomyDate?: string;
  lastSmearTestDate?: string;
  lastMammographyDate?: string;
  pregnancyCount?: number;
  hadAbnormalSmear?: boolean;
  currentContraceptiveMethod?: string;
  pastContraceptiveMethods?: string;
  lastMenstrualDate?: string;
  menstrualCycleLength?: number;
  childrenCount?: number;
  livingChildrenCount?: number;
  deceasedChildrenCount?: number;
  stillbornCount?: number;
  miscarriageCount?: number;
  deliveryType?: string;
  // Informations sur la grossesse actuelle
  dateDebutGrossesse?: string;
  dateAccouchementPrevu?: string;
  grossesseDesiree?: boolean;
  grossesseMultiple?: boolean;
  nombreFoetus?: number;
  groupeSanguin?: string;
  rh?: string;
  rci?: boolean;
  menaceAccouchementPremature?: boolean;
  diabeteGestationnel?: boolean;
  hypertensionArterielle?: boolean;
  preeclampsie?: boolean;
  autresPathologies?: string;
  traitementEnCours?: string;
  allergies?: string;
  antecedentsObstetricaux?: string;
  commentaires?: string;
}

// --- Informations pédiatriques ---
export interface PediatricsInfo {
  numeroDossier?: string;
  dateEnregistrement?: string;
  nomMere?: string;
  ageMere?: string;
  adresseMere?: string;
  telephoneMere?: string;
  fonctionMere?: string;
  nomPere?: string;
  agePere?: string;
  adressePere?: string;
  telephonePere?: string;
  fonctionPere?: string;
  formulaireCompletepar?: string;
  nomTuteurLegal?: string;
  autorisationParentale?: boolean;
  soinsPrenataux?: string;
  complicationsGrossesse?: string;
  tabacMedicamentsGrossesse?: boolean;
  lieuNaissance?: string;
  typeAccouchement?: string;
  poidsNaissance?: string;
  ageGestationnel?: string;
  complicationsAccouchement?: string;
  descriptionComplications?: string;
  histoireMedicaleGenerale?: string;
  descriptionTabacMedicamentsGrossesse?: string;
  medicamentsActuels?: string;
  allergiesPatient?: string;
  patientVitAvec?: string;
  vaGarderieEcole?: string;
}

// --- Dossier médical ---
export interface MedicalRecord {
  id: number;
  doctorId: number;
  patientId: number;
  patientFullName: string;
  consultationFee: number;
  amountPaid: number;
  amountDue: number;
  dateCreation: string;
  symptoms: string;
  motif?: string; // Reason for consultation - separate from symptoms
  diagnostic?: string;
  observations?: string;
  recommendation: string;
  additionalExaminations?: Array<{
    examName: string;
    reason: string;
    concernedDiagnostics: string;
    patientAdvice: string;
  }>;
  prescriptionItems?: SimplePrescriptionItem[];
  gynecologyInfo?: GynecologyInfo;
  pediatricsInfo?: PediatricsInfo;
  specialtyType?: string;
  constanteId?: number;
  prescriptionUrl?: string;
  resultUrl?: string;
  analysisNumber?: string;
  analysisResultUrl?: string;
  // Additional optional fields for UI compatibility
  phone?: string;
  doctorName?: string;
  symptomDuration?: string;
  currentMedication?: string;
  preexistingCondition?: string;
  familyHistory?: string;
  allergies?: string;
  assessment?: string;
}

// --- Consultation ---
export interface Consultation {
  id: number;
  patientId: number;
  doctorId: number | string;
  doctorName: string;
  montant: number;
  sommePaye: number;
  sommeImpaye: number;
  dateVisit: string;
  motif: VisitReason | string;
  // Optional/derived fields used by UI
  fullName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  telephone?: string;
  dateOfBirth?: string;
  maladeId?: number;
  examinationDate?: string;
  patient?: Patient;
  doctor?: Doctor;
}

// --- Patient + Constante (payload combiné) ---
export interface PatientWithConstante {
  patient: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    address: string | null;
    phone: string;
    email: string | null;
    doctorId: number;
    montant: number;
    sommePaye: number;
    dateVisit: string;
    motif: VisitReason;
  };
  constante?: MedicalConstant;
}

// --- ExtractedPatientData for summary cards ---
export interface ExtractedPatientData {
  fullName: string;
  firstName: string;
  lastName: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  gender: string;
  email: string;
  motif: string;
  poids: string;
  taille: string;
  tension: string;
  rythme: string;
  temperature: string;
  age: number | null;
}
